import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  useTheme
} from '@mui/material';
import { 
  BarChart as BarChartIcon, 
  PieChart as PieChartIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import { 
  getPaletteOptions, 
  getCurrentPaletteSelections, 
  setPalette,
  getPaletteById,
  getUserSetPalettes
} from '../../utils/colorMapping';

const ChartSettings = ({ 
  onPaletteChange, 
  compact = false, 
  defaultBarPalette = 'vibrant', 
  defaultPiePalette = 'colorful' 
}) => {
  const theme = useTheme();
  
  // Map config values to actual palette IDs
  const mapConfigToPaletteId = (configValue) => {
    const mapping = {
      'blues': 'blueShades',
      'greens': 'greenShades',
      'reds': 'redShades',
      'purples': 'purpleShades',
      'professional': 'professional',
      'vibrant': 'vibrant',
      'colorful': 'colorfulMix',
      'warm': 'warmColors',
      'cool': 'coolColors',
      'pastel': 'pastelColors'
    };
    return mapping[configValue] || configValue;
  };
  
  const [currentPalettes, setCurrentPalettes] = useState({ 
    bar: mapConfigToPaletteId(defaultBarPalette), 
    pie: mapConfigToPaletteId(defaultPiePalette) 
  });
  
  useEffect(() => {
    // Check if there are existing user-selected palettes
    const existingSelections = getCurrentPaletteSelections();
    const userSetFlags = getUserSetPalettes();
    
    // Only use config defaults if no user selections exist
    const barPalette = userSetFlags.bar ? existingSelections.bar : mapConfigToPaletteId(defaultBarPalette);
    const piePalette = userSetFlags.pie ? existingSelections.pie : mapConfigToPaletteId(defaultPiePalette);
    
    setCurrentPalettes({
      bar: barPalette,
      pie: piePalette
    });
    
    // Set the palettes in the color mapping utility only if they weren't user-selected
    if (!userSetFlags.bar) {
      setPalette('bar', barPalette, false); // false = not a user selection
    }
    if (!userSetFlags.pie) {
      setPalette('pie', piePalette, false); // false = not a user selection
    }
  }, [defaultBarPalette, defaultPiePalette]);

  const handlePaletteChange = (chartType, paletteId) => {
    setPalette(chartType, paletteId, true); // true = user selection
    const newPalettes = { ...currentPalettes, [chartType]: paletteId };
    setCurrentPalettes(newPalettes);
    
    if (onPaletteChange) {
      onPaletteChange(chartType, paletteId);
    }
  };

  const paletteOptions = getPaletteOptions();

  const ColorPreview = ({ paletteId, size = 16 }) => {
    const palette = getPaletteById(paletteId);
    if (!palette) return null;
    
    return (
      <Box sx={{ display: 'flex', gap: 0.3, mr: 1 }}>
        {palette.slice(0, 4).map((color, index) => (
          <Box
            key={index}
            sx={{
              width: size,
              height: size,
              backgroundColor: color.border,
              borderRadius: '2px',
              border: '1px solid rgba(0,0,0,0.1)',
              flexShrink: 0
            }}
          />
        ))}
      </Box>
    );
  };

  return compact ? (
    // Compact mode for integration into grid
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
      {/* Bar Chart Palette Selector */}
      <FormControl 
        size="small" 
        fullWidth
        sx={{ 
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: 2,
            pr: 1 // Ensure padding for dropdown arrow
          },
          '& .MuiSelect-select': {
            pr: 4 // Specific padding for select element to prevent overlap
          }
        }}
      >
        <InputLabel sx={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
          <BarChartIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Bar Charts
        </InputLabel>
        <Select
          value={currentPalettes.bar}
          label="Bar Charts"
          onChange={(e) => handlePaletteChange('bar', e.target.value)}
          renderValue={(value) => {
            const option = paletteOptions.find(p => p.id === value);
            return (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                pr: 2 // Add padding right to prevent overlap with dropdown arrow
              }}>
                <ColorPreview paletteId={value} size={10} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}
                >
                  {option?.name}
                </Typography>
              </Box>
            );
          }}
        >
          <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600 }}>
            Single-shade palettes
          </MenuItem>
          {paletteOptions.filter(p => p.type === 'single-shade').map((palette) => (
            <MenuItem key={palette.id} value={palette.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ColorPreview paletteId={palette.id} size={12} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {palette.name}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
          <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, mt: 0.5 }}>
            Colorful palettes
          </MenuItem>
          {paletteOptions.filter(p => p.type === 'colorful').map((palette) => (
            <MenuItem key={palette.id} value={palette.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ColorPreview paletteId={palette.id} size={12} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {palette.name}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Pie Chart Palette Selector */}
      <FormControl 
        size="small" 
        fullWidth
        sx={{ 
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: 2,
            pr: 1 // Ensure padding for dropdown arrow
          },
          '& .MuiSelect-select': {
            pr: 4 // Specific padding for select element to prevent overlap
          }
        }}
      >
        <InputLabel sx={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem' }}>
          <PieChartIcon sx={{ mr: 0.5, fontSize: 16 }} />
          Pie Charts
        </InputLabel>
        <Select
          value={currentPalettes.pie}
          label="Pie Charts"
          onChange={(e) => handlePaletteChange('pie', e.target.value)}
          renderValue={(value) => {
            const option = paletteOptions.find(p => p.id === value);
            return (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                pr: 2 // Add padding right to prevent overlap with dropdown arrow
              }}>
                <ColorPreview paletteId={value} size={10} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}
                >
                  {option?.name}
                </Typography>
              </Box>
            );
          }}
        >
          <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600 }}>
            Single-shade palettes
          </MenuItem>
          {paletteOptions.filter(p => p.type === 'single-shade').map((palette) => (
            <MenuItem key={palette.id} value={palette.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ColorPreview paletteId={palette.id} size={12} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {palette.name}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
          <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, mt: 0.5 }}>
            Colorful palettes
          </MenuItem>
          {paletteOptions.filter(p => p.type === 'colorful').map((palette) => (
            <MenuItem key={palette.id} value={palette.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <ColorPreview paletteId={palette.id} size={12} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    {palette.name}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  ) : (
    // Full mode for standalone usage
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2.5, 
        mb: 2,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, white 100%)`,
        border: `1px solid ${theme.palette.grey[200]}`
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <PaletteIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 24 }} />
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            fontSize: '1.1rem'
          }}
        >
          Chart Color Palettes
        </Typography>
      </Box>

      <Stack direction="row" spacing={3} alignItems="center">
        {/* Bar Chart Palette Selector */}
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2
            }
          }}
        >
          <InputLabel sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChartIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Bar Charts
          </InputLabel>
          <Select
            value={currentPalettes.bar}
            label="Bar Charts"
            onChange={(e) => handlePaletteChange('bar', e.target.value)}
            renderValue={(value) => {
              const option = paletteOptions.find(p => p.id === value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ColorPreview paletteId={value} />
                  {option?.name}
                </Box>
              );
            }}
          >
            <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600 }}>
              Single-shade palettes
            </MenuItem>
            {paletteOptions.filter(p => p.type === 'single-shade').map((palette) => (
              <MenuItem key={palette.id} value={palette.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ColorPreview paletteId={palette.id} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {palette.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {palette.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
            <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, mt: 0.5 }}>
              Colorful palettes
            </MenuItem>
            {paletteOptions.filter(p => p.type === 'colorful').map((palette) => (
              <MenuItem key={palette.id} value={palette.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ColorPreview paletteId={palette.id} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {palette.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {palette.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Pie Chart Palette Selector */}
        <FormControl 
          size="small" 
          sx={{ 
            minWidth: 200,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
              borderRadius: 2
            }
          }}
        >
          <InputLabel sx={{ display: 'flex', alignItems: 'center' }}>
            <PieChartIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Pie Charts
          </InputLabel>
          <Select
            value={currentPalettes.pie}
            label="Pie Charts"
            onChange={(e) => handlePaletteChange('pie', e.target.value)}
            renderValue={(value) => {
              const option = paletteOptions.find(p => p.id === value);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ColorPreview paletteId={value} />
                  {option?.name}
                </Box>
              );
            }}
          >
            <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600 }}>
              Single-shade palettes
            </MenuItem>
            {paletteOptions.filter(p => p.type === 'single-shade').map((palette) => (
              <MenuItem key={palette.id} value={palette.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ColorPreview paletteId={palette.id} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {palette.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {palette.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
            <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 600, mt: 0.5 }}>
              Colorful palettes
            </MenuItem>
            {paletteOptions.filter(p => p.type === 'colorful').map((palette) => (
              <MenuItem key={palette.id} value={palette.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ColorPreview paletteId={palette.id} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {palette.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {palette.description}
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Quick preview of selected palettes */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Preview:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BarChartIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
              <ColorPreview paletteId={currentPalettes.bar} size={14} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PieChartIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
              <ColorPreview paletteId={currentPalettes.pie} size={14} />
            </Box>
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ChartSettings;
